import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  Shield,
  AlertTriangle,
  Trash2,
  Briefcase,
  MapPin,
  Heart,
  FileText,
} from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { Badge } from '../components/Badge';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { CreateProviderProfileModal } from '../components/CreateProviderProfileModal';
import { EditUserProfileModal } from '../components/EditUserProfileModal';
import { DeleteAccountModal } from '../components/DeleteAccountModal';
import { DeletionSuccessModal } from '../components/DeletionSuccessModal';
import { useAuth } from '../context/AuthContext';
import { getProviderProfile, createProviderProfile, getUserProfile, updateUserProfile } from '../services/api';
import { ProviderProfile, ProviderProfileFormData, UserProfile, UpdateUserProfileData } from '../types';
import { ROUTES } from '../constants';

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user, deleteAccount, logout, updateProviderProfileStatus } = useAuth();

  if (user?.role === 'admin') {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  const isProvider = user?.role === 'provider';
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeletionSuccessModal, setShowDeletionSuccessModal] = useState(false);
  const [providerProfile, setProviderProfile] = useState<ProviderProfile | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [showEditUserProfileModal, setShowEditUserProfileModal] = useState(false);

  useEffect(() => {
    if (isProvider && user?.userId) {
      fetchProviderProfile();
    } else if (!isProvider && user?.userId) {
      fetchUserProfile();
    }
  }, [isProvider, user?.userId]);

  const fetchProviderProfile = async () => {
    if (!user?.userId) return;

    setLoadingProfile(true);
    try {
      const response = await getProviderProfile(user.userId);
      if (response.success && response.provider) {
        setProviderProfile(response.provider);

        // Check if profile is complete
        const profile = response.provider;
        const typeOfCareArray = Array.isArray(profile.typeOfCare)
          ? profile.typeOfCare
          : (typeof profile.typeOfCare === 'string' && profile.typeOfCare.trim() !== '')
            ? [profile.typeOfCare]
            : [];

        const isComplete = Boolean(
          typeOfCareArray.length > 0 &&
          !typeOfCareArray.includes('Not specified') &&
          profile.city &&
          profile.city.trim() !== '' &&
          profile.city !== 'Not specified' &&
          profile.city !== 'Not set' &&
          Array.isArray(profile.services) &&
          profile.services.length > 0 &&
          profile.serviceRadiusKm &&
          profile.serviceRadiusKm > 0 &&
          profile.pricing &&
          profile.pricing.hourly &&
          profile.pricing.hourly > 0
        );

        setHasProfile(isComplete);
        setShowProfileModal(!isComplete);
      } else {
        setHasProfile(false);
        setShowProfileModal(true);
      }
    } catch (err) {
      console.error('Failed to fetch provider profile:', err);
      setHasProfile(false);
      setShowProfileModal(true);
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchUserProfile = async () => {
    if (!user?.userId) return;

    setLoadingProfile(true);
    try {
      const response = await getUserProfile();
      if (response.success && response.profile) {
        setUserProfile(response.profile);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    } finally {
      setLoadingProfile(false);
    }
  };

  const handleProfileSubmit = async (formData: ProviderProfileFormData) => {
    const response = await createProviderProfile(formData);

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error || 'Failed to create profile'
    };
  };

  const handleProfileSuccess = () => {
    setShowProfileModal(false);
    setHasProfile(true);
    updateProviderProfileStatus(true);
    fetchProviderProfile();
  };

  const handleDeleteAccount = async (reason: string) => {
    const result = await deleteAccount(reason);

    if (result.success) {
      setShowDeleteModal(false);
      setTimeout(() => setShowDeletionSuccessModal(true), 0);
    }

    return result;
  };

  const handleEditUserProfile = () => {
    setShowEditUserProfileModal(true);
  };

  const handleUserProfileUpdate = async (profileData: UpdateUserProfileData) => {
    console.log('=== Settings: handleUserProfileUpdate called ===');
    console.log('ProfileData received:', {
      medicalConditions: profileData.medicalConditions,
      languagesPreferred: profileData.languagesPreferred,
      preferredCareTypes: profileData.preferredCareTypes,
      ...profileData
    });

    const response = await updateUserProfile(profileData);

    console.log('API response:', response);

    if (response.success) {
      return { success: true };
    }

    return {
      success: false,
      error: response.error || 'Failed to update profile'
    };
  };

  const handleUserProfileUpdateSuccess = () => {
    setShowEditUserProfileModal(false);
    fetchUserProfile();
  };

  const bgClass = isProvider ? 'bg-gradient-to-br from-green-50 via-white to-green-50' : 'bg-gradient-to-br from-blue-50 via-white to-blue-50';
  const iconBg = isProvider ? 'bg-green-100' : 'bg-blue-100';
  const iconColor = isProvider ? 'text-green-600' : 'text-blue-600';
  const hoverBg = isProvider ? 'hover:bg-green-50' : 'hover:bg-blue-50';

  return (
    <div className={`min-h-screen ${bgClass} relative`}>
      {isProvider && !hasProfile && (
        <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40" />
      )}

      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 overflow-hidden">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                onClick={() => navigate(ROUTES.HOME_PAGE)}
                className={`p-2 text-gray-600 hover:text-gray-900 ${hoverBg} rounded-lg transition-colors flex-shrink-0`}
                aria-label="Back to home"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex-shrink-0 min-w-0">
                <Logo size="md" />
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Settings</h1>
          <p className="text-gray-600">
            Manage your {isProvider ? 'business' : 'account'} settings and preferences
          </p>
        </div>

        {isProvider && !hasProfile && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-600 p-4 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <FileText className="h-5 w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Profile Completion Required
                </p>
                <p className="mt-1 text-sm text-green-700">
                  Please complete your provider profile to access all features and settings.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6">
          {isProvider && (
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`p-2 ${iconBg} rounded-lg`}>
                    <User className={`h-5 w-5 ${iconColor}`} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Account Information</h2>
                </div>
                <div className="flex items-center gap-1">
                  <Shield className="h-4 w-4 text-green-600" />
                  <Badge variant="success">CQC Verified</Badge>
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-900">{user?.name || 'N/A'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Mail className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-900">{user?.email || 'N/A'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-900">{user?.phone || 'Not provided'}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Member Since
                    </label>
                    <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="h-4 w-4 text-gray-500" />
                      <span className="text-gray-900">
                        {user?.createdAt
                          ? new Date(user.createdAt).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'long',
                            })
                          : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button variant="primary" onClick={() => {}}>
                    Edit Profile
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {isProvider && (
            <Card className={!hasProfile ? 'opacity-50 pointer-events-none' : ''}>
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Briefcase className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Business Information</h2>
              </div>

              {loadingProfile ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Types of Care
                      </label>
                      <div className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Briefcase className="h-4 w-4 text-gray-500 mt-1 flex-shrink-0" />
                          <div className="flex flex-wrap gap-2">
                            {providerProfile?.typeOfCare ? (
                              Array.isArray(providerProfile.typeOfCare) ? (
                                providerProfile.typeOfCare.map((type, index) => (
                                  <Badge key={index} variant="primary">{type}</Badge>
                                ))
                              ) : (
                                <Badge variant="primary">{providerProfile.typeOfCare}</Badge>
                              )
                            ) : (
                              <span className="text-gray-500">Not set</span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Location
                      </label>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-900">
                          {providerProfile?.location || 'Not set'}
                        </span>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Service Radius
                      </label>
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <MapPin className="h-4 w-4 text-gray-500" />
                        <span className="text-gray-900">
                          {providerProfile?.serviceRadiusKm
                            ? `${providerProfile.serviceRadiusKm} km`
                            : 'Not set'}
                        </span>
                      </div>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Services Offered
                      </label>
                      {providerProfile && providerProfile.services.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {providerProfile.services.map((service, index) => (
                            <Badge key={index} variant="info">
                              {service}
                            </Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No services set</p>
                      )}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button
                      variant="primary"
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => navigate(ROUTES.PROVIDER_PROFILE)}
                    >
                      View Full Profile
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          )}

          {!isProvider && (
            <Card>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Account & Profile Information</h2>
                </div>
                {user?.isProfileCompleted && (
                  <Badge variant="success">Complete</Badge>
                )}
              </div>

              {loadingProfile ? (
                <div className="flex items-center justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : userProfile ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Account Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <User className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-900">{user?.name || 'N/A'}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Mail className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-900">{user?.email || 'N/A'}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Phone className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-900">{user?.phone || userProfile.profile?.phone || 'Not provided'}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Member Since</label>
                        <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                          <Calendar className="h-4 w-4 text-gray-500" />
                          <span className="text-gray-900">
                            {user?.createdAt
                              ? new Date(user.createdAt).toLocaleDateString('en-GB', {
                                  year: 'numeric',
                                  month: 'long',
                                })
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    {userProfile.profile?.avatar && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Profile Photo</label>
                        <img
                          src={userProfile.profile.avatar}
                          alt="Profile"
                          className="w-16 h-16 rounded-full object-cover border-2 border-blue-100"
                        />
                      </div>
                    )}
                    {userProfile.profile?.bio && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
                        <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">{userProfile.profile.bio}</p>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Address
                    </h3>
                    <div className="space-y-2 p-3 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-900">{userProfile.address}</p>
                      <p className="text-sm text-gray-600">{userProfile.city}, {userProfile.postalCode}</p>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      Care Recipient Details
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">{userProfile.recipientName}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Age</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">{userProfile.recipientAge}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">{userProfile.recipientGender}</span>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Mobility Status</label>
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">{userProfile.mobilityStatus}</span>
                        </div>
                      </div>
                    </div>
                    {userProfile.equipmentDetails && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Equipment Details</label>
                        <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded-lg">{userProfile.equipmentDetails}</p>
                      </div>
                    )}
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Medical Information
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Medical Conditions</label>
                      {userProfile.medicalConditions && userProfile.medicalConditions.length > 0 ? (
                        <div className="flex flex-wrap gap-2">
                          {userProfile.medicalConditions.map((condition, index) => (
                            <Badge key={index} variant="info">{condition}</Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No medical conditions listed</p>
                      )}
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Emergency Contact
                    </h3>
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <p className="text-gray-900 font-semibold">{userProfile.emergencyContactName}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                        <p className="text-gray-900 font-semibold">{userProfile.emergencyContactPhone}</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Preferences</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Care Types</label>
                        {userProfile.preferredCareTypes && userProfile.preferredCareTypes.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {userProfile.preferredCareTypes.map((type, index) => (
                              <Badge key={index} variant="info">{type}</Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No care type preferences set</p>
                        )}
                      </div>
                      {userProfile.languagesPreferred && userProfile.languagesPreferred.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Languages Preferred</label>
                          <div className="flex flex-wrap gap-2">
                            {userProfile.languagesPreferred.map((language, index) => (
                              <Badge key={index} variant="primary">{language}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-4">
                    <Button variant="primary" onClick={handleEditUserProfile}>Edit Profile</Button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-600 mb-4">No profile information available</p>
                  <Button variant="primary" onClick={() => navigate(ROUTES.HOME_PAGE)}>
                    Complete Your Profile
                  </Button>
                </div>
              )}
            </Card>
          )}

          <Card>
            <div className="flex items-center gap-3 mb-6">
              <div className={`p-2 ${iconBg} rounded-lg`}>
                <Shield className={`h-5 w-5 ${iconColor}`} />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Security</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <p className="text-sm text-gray-600 mb-3">
                  Update your password to keep your account secure
                </p>
                <Button variant="outline" onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}>
                  Change Password
                </Button>
              </div>
            </div>
          </Card>

          <Card className="border-2 border-red-200 bg-red-50">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-red-900">Danger Zone</h2>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-red-900 mb-2">Delete Account</h3>
                <p className="text-sm text-red-800 mb-4">
                  Once you delete your account, there is no going back. All your data,
                  including bookings, preferences, and account information will be permanently
                  removed.
                </p>
                <Button
                  variant="outline"
                  className="border-red-600 text-red-600 hover:bg-red-600 hover:text-white"
                  onClick={() => setShowDeleteModal(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Account
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </main>

      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirmDelete={handleDeleteAccount}
        userRole={isProvider ? 'provider' : 'service_user'}
      />

      <DeletionSuccessModal
        isOpen={showDeletionSuccessModal}
        onClose={async () => {
          setShowDeletionSuccessModal(false);
          await logout();
        }}
      />

      {isProvider && (
        <CreateProviderProfileModal
          isOpen={showProfileModal}
          providerId={user?.userId || (user?.providerId ? String(user.providerId) : '')}
          onSuccess={handleProfileSuccess}
          onSubmit={handleProfileSubmit}
          {...(hasProfile && providerProfile ? {
            mode: 'edit' as const,
            initialData: providerProfile,
            onClose: () => setShowProfileModal(false)
          } : {})}
        />
      )}

      {!isProvider && userProfile && (
        <EditUserProfileModal
          isOpen={showEditUserProfileModal}
          onClose={() => setShowEditUserProfileModal(false)}
          profile={userProfile}
          onSubmit={handleUserProfileUpdate}
          onSuccess={handleUserProfileUpdateSuccess}
        />
      )}
    </div>
  );
};
